import { useState } from 'react'
import { Alert, Button, Group, Modal, Select, Stack, Text } from '@mantine/core'
import { DateTimePicker } from '@mantine/dates'
import { IconAlertCircle, IconPlayerPlay } from '@tabler/icons-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'
import { TaskArgsEditor, type ArgsEditorState } from './TaskArgsEditor'
import {
  FOLLOWUP_ACTION_OPTIONS,
  type FollowupAction,
  type RunSelection,
  type TaskInfo,
} from './hooks'

interface RunTaskModalProps {
  task: TaskInfo
  onClose: () => void
  onScheduled: (selection: RunSelection) => void
}

/** Mantine v8 DateTimePicker emits a local 'YYYY-MM-DD HH:mm:ss' string (or null). */
function pickerValueToIso(value: string | null): string | undefined {
  return value ? new Date(value.replace(' ', 'T')).toISOString() : undefined
}

/**
 * Schedules a new run: the task's registered args component (seeded with
 * scheduled_args) or a raw JSON textarea fallback, live-validated so Run only
 * enables on valid args, plus an optional future run time. Mounted fresh on each
 * open so the state re-seeds. See docs/tasks/HOWTO_TASK.md.
 */
export function RunTaskModal({ task, onClose, onScheduled }: RunTaskModalProps) {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const [args, setArgs] = useState<ArgsEditorState>({ value: undefined, error: null })
  const [when, setWhen] = useState<string | null>(null)
  const [followupAction, setFollowupAction] = useState<FollowupAction>(task.followupAction)

  const runTask = useMutation(
    orpc.runTask.mutationOptions({
      onSuccess: async ({ id }) => {
        await queryClient.invalidateQueries({ queryKey: orpc.listTaskRuns.key() })
        onScheduled({ slug: task.slug, id })
        onClose()
      },
    }),
  )

  return (
    <Modal opened onClose={onClose} title={<Text fw={600}>Run {task.slug}</Text>} size="lg">
      <Stack gap="sm">
        <TaskArgsEditor slug={task.slug} initialValue={task.scheduledArgs} onChange={setArgs} />
        <DateTimePicker
          label="Run at"
          // No date picked = run now. The placeholder is styled as a regular value so the
          // field reads "Now" rather than looking like an empty text input; picking a date
          // replaces it, and the clear button returns to Now.
          placeholder="Now"
          classNames={{ input: '[&_.mantine-InputPlaceholder-placeholder]:text-inherit' }}
          highlightToday
          clearable
          value={when}
          onChange={setWhen}
          valueFormat="MM/DD/YYYY, hh:mm A"
          // withinPortal: false keeps the time dropdown inside the date popover's DOM —
          // portalled, its clicks count as outside clicks and close the whole picker.
          timePickerProps={{
            format: '12h',
            withDropdown: true,
            popoverProps: { withinPortal: false },
          }}
        />
        <Select
          label="Follow-up action"
          description="What happens to the follow-up tasks this run may return"
          data={FOLLOWUP_ACTION_OPTIONS}
          value={followupAction}
          onChange={(value) => value && setFollowupAction(value as FollowupAction)}
          allowDeselect={false}
        />
        {runTask.error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {runTask.error.message}
          </Alert>
        )}
        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            leftSection={<IconPlayerPlay size={14} />}
            disabled={args.error !== null}
            loading={runTask.isPending}
            onClick={() =>
              runTask.mutate({
                slug: task.slug,
                args: args.value,
                when: pickerValueToIso(when),
                followupAction,
              })
            }
          >
            Run
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
